import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';
import { loadCookie } from 'globals/cookieHelper';

import * as chatActions from '../../app/ChatApp/actions';
import Poller from '../../../../api/connect/poller';
import * as chatSelectors from '../../app/ChatApp/selectors';

const ONE_SECOND = 1000;
const ONE_MINUTE = 60 * ONE_SECOND;

const DEFAULT_OBJECT = {};

export class ChatConversationQueuePoller extends React.PureComponent {
  constructor(props) {
    super(props);

    this.pollerForQueue = null;
    this.pollerForEstimatedWaitTime = null;
  }

  componentDidMount() {
    this.maybeStartPollerForQueue();
  }

  componentDidUpdate(prevProps) {
    const { sessionId, vcFormTriggered, clearQueueEntities } = this.props;

    if (sessionId !== 'new' && prevProps.sessionId === 'new') {
      this.maybeStartPollerForQueue();
    }

    if (vcFormTriggered && !prevProps.vcFormTriggered) {
      this.stopPolling();
      clearQueueEntities();
    }
  }

  componentWillUnmount() {
    if (this.pollerForQueue) clearTimeout(this.pollerForQueue);
    this.stopPolling();
  }

  maybeStartPollerForQueue() {
    const { isLocked, sessionId, upfrontVolumeControlEnabled, vcFormTriggered } = this.props;

    const shouldNotPoll = isLocked || sessionId === 'new' || !upfrontVolumeControlEnabled || vcFormTriggered;

    if (shouldNotPoll) return;

    this.pollForQueue();
  }

  startQueuePolling() {
    this.pollerForEstimatedWaitTime = new Poller({
      task: millisSinceLastPoll => this.pollQueue(millisSinceLastPoll),
      defaultInterval: 10000,
    });
    this.pollerForEstimatedWaitTime.start();
  }

  stopPolling() {
    // eslint-disable-next-line no-unused-expressions
    this.pollerForEstimatedWaitTime && this.pollerForEstimatedWaitTime.stop();
  }

  updateInterval(queueData = DEFAULT_OBJECT) {
    if (!this.pollerForEstimatedWaitTime && !this.pollerForEstimatedWaitTime.setMaxInterval) return DEFAULT_OBJECT;
    const estimatedWaitTimeSeconds = queueData.estimatedWaitTime;
    const waitTime = estimatedWaitTimeSeconds / 60;

    if (waitTime === 0) {
      this.pollerForEstimatedWaitTime.stop();
      return queueData;
    }

    if (waitTime < ONE_MINUTE) {
      this.pollerForEstimatedWaitTime.setMaxInterval(0.1 * ONE_MINUTE);
    } else if (waitTime > ONE_MINUTE && waitTime < 5 * ONE_MINUTE) {
      this.pollerForEstimatedWaitTime.setMaxInterval(0.5 * ONE_MINUTE);
    } else if (waitTime > 5 * ONE_MINUTE && waitTime < 10 * ONE_MINUTE) {
      this.pollerForEstimatedWaitTime.setMaxInterval(ONE_MINUTE);
    } else {
      this.pollerForEstimatedWaitTime.setMaxInterval();
    }

    return queueData;
  }

  pollForQueue() {
    const { fetchQueue, sessionId } = this.props;
    const POLL_TIME = 3000;
    const POLL_FOR = 60000;
    let attempts = 0;

    this.pollerForQueue = () =>
      setTimeout(() => {
        return fetchQueue(sessionId)
          .then(() => this.startQueuePolling())
          .catch(() => {
            if (POLL_TIME * attempts < POLL_FOR) {
              attempts += 1;
              this.pollerForQueue();
            }
          });
      }, POLL_TIME);

    this.pollerForQueue();
  }

  pollQueue() {
    const { pollQueue, sessionId } = this.props;

    return pollQueue(sessionId)
      .then(this.updateInterval.bind(this))
      .catch(() => {
        this.stopPolling();
      });
  }

  render() {
    return <div />;
  }
}

ChatConversationQueuePoller.propTypes = {
  pollQueue: PropTypes.func,
  fetchQueue: PropTypes.func,
  clearQueueEntities: PropTypes.func,
  sessionId: PropTypes.string,
  isLocked: PropTypes.bool,
  upfrontVolumeControlEnabled: PropTypes.bool,
  vcFormTriggered: PropTypes.bool,
};

ChatConversationQueuePoller.defaultProps = {
  sessionId: 'new',
  isLocked: false,
  upfrontVolumeControlEnabled: false,
  vcFormTriggered: false,
};

const mapStateToProps = state => {
  const sessionId = chatSelectors.getActiveSessionId(state);
  return {
    upfrontVolumeControlEnabled: chatSelectors.getUpfrontVolumeControlEnabled(state),
    isLocked: chatSelectors.isSessionLocked(state, sessionId),
    vcFormTriggered: !!loadCookie(`input-disabled-${sessionId}`),
    sessionId,
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    fetchQueue(sessionId) {
      return dispatch(chatActions.fetchQueue(sessionId));
    },
    pollQueue(sessionId) {
      return dispatch(chatActions.pollForQueue(sessionId));
    },
    clearQueueEntities() {
      return dispatch(chatActions.clearSessionQueueEntities());
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ChatConversationQueuePoller);
